import { createReducer } from '@reduxjs/toolkit';
import { authActions } from 'features/auth/redux/auth.actions';
import { PartnerNameEnum } from 'shared/enums/partner-name-enum';
import { extractUserDataFromJwtToken } from 'utils/extract-user-data-from-jwt-token';
import { getAccessTokenFromLocalStorage } from 'utils/local-storage';
import { PermissionsAdminEnum } from 'features/auth/enums/permissions-admin.enum';
import { UserStatusEnum } from 'features/admin-users/enums/user-status.enum';

export const initialState = {
  loadingStatus: false as boolean,
  isAuth: false as boolean,
  checkingAuth: true as boolean,
  username: null as string | null,
  permissions: [] as PermissionsAdminEnum[],
  partner: null as PartnerNameEnum | null,
  status: null as UserStatusEnum | null,
  isSuperAdmin: false as boolean,
  error: {
    login: [] as string[],
    logout: [] as string[],
  },
  role: {} as { id: number; name: string },
  id: null as number | null,
  userEmail: null as string | null,
  isEmailVerificationRequired: false as boolean,
  isEmailVerified: false as boolean,
  signOutLoadingStatus: false as boolean,
} as const;

const authReducer = createReducer(initialState, (builder) => {
  builder.addCase(authActions.clearErrors, (state) => {
    return {
      ...state,
      error: { ...initialState.error },
    };
  });
  builder.addCase(authActions.checkAuthRequest, (state) => {
    return {
      ...state,
      loadingStatus: true,
      checkingAuth: true,
    };
  });
  builder.addCase(authActions.checkAuthSuccess, (state) => {
    const userData = extractUserDataFromJwtToken(
      getAccessTokenFromLocalStorage()
    );
    return {
      ...state,
      checkingAuth: false,
      isAuth: true,
      username: userData.username,
      permissions: userData.permissions as PermissionsAdminEnum[],
      partner: userData.partnerName as PartnerNameEnum,
      loadingStatus: false,
      isSuperAdmin: !!userData?.permissions?.includes(
        PermissionsAdminEnum.SUPER_ADMIN
      ),
      id: userData.id,
      role: userData.role,
      status: userData.status as UserStatusEnum,
      userEmail: userData.email,
      isEmailVerificationRequired: userData.isEmailVerificationRequired,
      isEmailVerified: userData.isEmailVerified,
    };
  });
  builder.addCase(authActions.checkAuthFailure, () => {
    return {
      ...initialState,
      checkingAuth: false,
    };
  });

  builder.addCase(authActions.signInRequest, (state) => {
    return {
      ...state,
      loadingStatus: true,
    };
  });
  builder.addCase(authActions.signInSuccess, (state) => {
    const userData = extractUserDataFromJwtToken(
      getAccessTokenFromLocalStorage()
    );
    return {
      ...state,
      checkingAuth: false,
      status: userData.status as UserStatusEnum,
      isAuth: true,
      username: userData.username,
      permissions: userData.permissions as PermissionsAdminEnum[],
      error: { ...state.error, login: [] },
      partner: userData.partnerName as PartnerNameEnum,
      loadingStatus: false,
      isSuperAdmin: !!userData?.permissions?.includes(
        PermissionsAdminEnum.SUPER_ADMIN
      ),
      id: userData.id,
      role: userData.role,
      userEmail: userData.email,
      isEmailVerificationRequired: userData.isEmailVerificationRequired,
      isEmailVerified: userData.isEmailVerified,
    };
  });
  builder.addCase(authActions.signInFailure, (state, action) => {
    return {
      ...initialState,
      error: { ...state.error, login: action.payload },
      checkingAuth: false,
    };
  });
  builder.addCase(authActions.signOutRequest, (state) => {
    return {
      ...state,
      signOutLoadingStatus: true,
      error: { ...state.error, logout: [] },
    };
  });
  builder.addCase(authActions.signOutSuccess, () => {
    return {
      ...initialState,
      checkingAuth: false,
    };
  });
  builder.addCase(authActions.signOutFailure, (state, action) => {
    return {
      ...state,
      error: { ...state.error, logout: action.payload },
      signOutLoadingStatus: false,
    };
  });

  builder.addCase(authActions.refreshTokenSuccess, (state, action) => {
    const userData = extractUserDataFromJwtToken(action.payload);
    return {
      ...state,
      checkingAuth: false,
      status: userData.status as UserStatusEnum,
      isAuth: true,
      username: userData.username,
      permissions: userData.permissions as PermissionsAdminEnum[],
      error: { ...state.error, login: [] },
      partner: userData.partnerName as PartnerNameEnum,
      loadingStatus: false,
      isSuperAdmin: !!userData?.permissions?.includes(
        PermissionsAdminEnum.SUPER_ADMIN
      ),
      id: userData.id,
      role: userData.role,
      userEmail: userData.email,
      isEmailVerificationRequired: userData.isEmailVerificationRequired,
      isEmailVerified: userData.isEmailVerified,
    };
  });
});

export default authReducer;
